/*
 * Calendar-day arithmetic in an IANA time zone (Intl only, no extra library): "the start of 24 October in Kyiv", "the same
 * wall clock a week ago". Days are not 24 hours long where the clocks change (Kyiv: 23 or 25 hours twice a year), so a
 * day, a week, a month or a year is always added to the date of the wall clock and turned back into an instant — never
 * as a number of milliseconds. Zone ids are current IANA names (see ./timeZones).
 */
import { intlTimeZone } from './timeZones';

/** Wall-clock parts in a time zone (month 1–12). */
export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: intlTimeZone(timeZone),
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** Whether the runtime knows the IANA zone. */
export function isKnownTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false;
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** The wall clock of an instant in a zone. */
export function toWallClock(instant: Date | number, timeZone: string): WallClock {
  const ms = typeof instant === 'number' ? instant : instant.getTime();
  const parts = formatter(timeZone).formatToParts(ms);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

const asUtc = (w: WallClock) => Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);

/** Offset of the zone at an instant, in minutes east of UTC. */
function offsetMinutes(ms: number, timeZone: string): number {
  const rounded = Math.floor(ms / 1000) * 1000;
  return Math.round((asUtc(toWallClock(rounded, timeZone)) - rounded) / 60_000);
}

/**
 * The instant of a wall clock in a zone. A time the clocks skip moves forward by the gap; an ambiguous time (the clocks
 * turned back) takes the earlier of the two instants.
 */
export function fromWallClock(wall: WallClock, timeZone: string): Date {
  const guess = asUtc(wall);
  const matches = (candidate: number) => asUtc(toWallClock(candidate, timeZone)) === guess;
  const first = guess - offsetMinutes(guess, timeZone) * 60_000;
  const second = guess - offsetMinutes(first, timeZone) * 60_000;
  if (first === second) return new Date(matches(first - 3_600_000) ? first - 3_600_000 : first);
  if (matches(Math.min(first, second))) return new Date(Math.min(first, second));
  if (matches(Math.max(first, second))) return new Date(Math.max(first, second));
  return new Date(Math.max(first, second));
}

/** Days in a month (month 1–12). */
export const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * The wall clock moved by whole calendar units: months and years keep the day of the month, or the last day of a shorter
 * month (31 March − 1 month = 28/29 February); days are plain date arithmetic. The time of day stays.
 */
export function addCalendar(
  wall: WallClock,
  amount: { days?: number; months?: number; years?: number },
): WallClock {
  const totalMonths = wall.year * 12 + (wall.month - 1) + (amount.months ?? 0) + (amount.years ?? 0) * 12;
  const year = Math.floor(totalMonths / 12);
  const month = (totalMonths % 12) + 1;
  const day = Math.min(wall.day, daysInMonth(year, month));
  const shifted = new Date(Date.UTC(year, month - 1, day + (amount.days ?? 0)));
  return {
    ...wall,
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/** `YYYY-MM-DD` → its parts, or null for anything else (including dates that do not exist). */
export function parseCalendarDate(
  value: string | null | undefined,
): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value ?? '');
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

/** The instant a calendar day (`YYYY-MM-DD`) starts in a zone (null for a malformed date), `plusDays` days later. */
export function startOfZonedDay(date: string, timeZone: string, plusDays = 0): Date | null {
  const parsed = parseCalendarDate(date);
  if (!parsed) return null;
  const wall = addCalendar({ ...parsed, hour: 0, minute: 0, second: 0 }, { days: plusDays });
  return fromWallClock(wall, timeZone);
}
